import * as React from 'react';

// API
import apibridge from '../../apibridge';

// UTILS
import { validationErrorsToString } from '../../utils';

// PACKAGES
import { SwitchTransition, CSSTransition } from 'react-transition-group';

// COMPONENTS
import Button from '../../components/Button';
import LoadingSpinner from '../../components/LoadingSpinner';

// ICONS
import { ReactComponent as AlertIcon } from '../../assets/svg/alert.svg';

// TYPES
import { closeModalType } from '../../components/GlobalModal';

interface DownloadContactListModalTypes {
	closeModal?: closeModalType;
}

const DownloadContactListModal: React.FC<DownloadContactListModalTypes> = ({ closeModal }) => {
	// Error and Loading state
	const [formErrorMessage, setFormErrorMessage] = React.useState<string>('');

	const [isLoading, setIsLoading] = React.useState<boolean>(false);

	const getContactList = async () => {
		setIsLoading(true);

		const response = await apibridge.getDashboardContactlistdownload();

		if (response && !response.isError) {
			// Type assertion after checking isError is false
			const successResponse = response as { result?: { contentType?: string; file?: string; fileName?: string } };
			if (successResponse.result) {
				const { contentType: mimeType, file: bytesBase64, fileName } = successResponse.result;

				// construct a url from the base64 and then fetch it like a file to access the blob method
				const fileUrl = 'data:' + mimeType + ';base64,' + bytesBase64;
				const fileResponse = await fetch(fileUrl);

				let blob;

				if (fileResponse && fileResponse.ok) {
					blob = await fileResponse.blob();
				}

				if (blob) {
					const url = window.URL.createObjectURL(blob);

					const a = document.createElement('a');

					a.style.display = 'none';

					a.href = url;

					a.download = fileName || 'contact-list.csv';

					document.body.appendChild(a);

					a.click();

					document.body.removeChild(a);
				} else {
					console.error('Blob creation failed');
				}
			}
		} else if (response.isError) {
			setFormErrorMessage(
				response.validationErrors.length
					? validationErrorsToString(response.validationErrors)
					: response.message || 'An error has occurred'
			);
		}

		setIsLoading(false);
	};

	return (
		<div className="modal-confirmation">
			{!!formErrorMessage ? (
				<div className="form-validation-error">{formErrorMessage}</div>
			) : (
				<SwitchTransition mode="out-in">
					<CSSTransition key={`key_${isLoading}`} timeout={600} classNames="standalone-fade" mountOnEnter unmountOnExit>
						{isLoading ? (
							<LoadingSpinner show={isLoading} />
						) : (
							<>
								<figure className="icon">
									<AlertIcon />
								</figure>

								<h2 className="heading h4">
									You are about to download a .csv file containing a contact list of <strong>all users</strong> in your
									organisation which contains <strong>confidential information</strong>.
								</h2>

								<p className="heading text-red font-weight-bold mt-4 mb-4">
									Please ensure that this information is stored securely.
								</p>

								<section className="actions mt-0">
									<Button className="btn-primary full-width" onClick={getContactList} disabled={isLoading}>
										Download
									</Button>

									<Button className="btn-secondary full-width" onClick={closeModal} disabled={isLoading}>
										Cancel
									</Button>
								</section>
							</>
						)}
					</CSSTransition>
				</SwitchTransition>
			)}
		</div>
	);
};

export default DownloadContactListModal;
